import React, { useState, useEffect } from "react";
import { ReactComponent as NavbarLogo } from "../../assets/svg/Landing/NavbarLogo.svg";
import { ReactComponent as ActiveNavbar } from "../../assets/svg/Landing/ActiveNavbar.svg";
import { Link } from "react-router-dom";
import { useAuthValidation } from "../../hooks/useAuthValidation";
import Loader from "../Live/Loader";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isValid, loading, user } = useAuthValidation();
  const [activeSection, setActiveSection] = useState("/");

  // Function to check if the link is active
  const isActive = (path) => {
    return activeSection === path;
  };

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      // Check which section is currently in view
      const aboutSection = document.getElementById("about");
      const contactSection = document.getElementById("contact-section");

      if (contactSection && window.scrollY >= contactSection.offsetTop - 100) {
        setActiveSection("/contact");
      } else if (
        aboutSection &&
        window.scrollY >= aboutSection.offsetTop - 100
      ) {
        setActiveSection("/about");
      } else {
        setActiveSection("/");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Function to scroll to section
  const scrollToSection = (sectionId) => {
    setIsMenuOpen(false); // Close mobile menu if open
    const section = document.getElementById(sectionId);
    if (section) {
      window.scrollTo({
        top: section.offsetTop - 80, // Offset for navbar height
        behavior: "smooth",
      });
    } else if (sectionId === "top") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <nav
      className={`bg-white ${
        scrolled ? "shadow-md" : ""
      } top-0 left-0 w-full z-50 transition-colors duration-300 sticky`}
    >
      <div className="container mx-auto px-4 xs:px-5 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 xs:h-18 sm:h-20 px-0 xs:px-2 sm:px-4 md:px-8 lg:px-12 xl:px-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <button
              onClick={() => scrollToSection("top")}
              className="flex items-center"
            >
              <NavbarLogo className="h-10 xs:h-12 sm:h-14 w-auto" />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden sl:block">
            <ul className="flex space-x-8 md:space-x-12 lg:space-x-16 xl:space-x-20">
              <li className="relative">
                <button
                  onClick={() => scrollToSection("top")}
                  className={`text-base md:text-md xl:text-lg font-medium transition duration-300 ${
                    isActive("/")
                      ? "text-primary"
                      : "text-gray-700 hover:text-blue-800"
                  }`}
                >
                  Home
                </button>
                {isActive("/") && (
                  <div className="absolute -bottom-7 left-1/2 transform -translate-x-1/2">
                    <ActiveNavbar className="w-auto h-auto" />
                  </div>
                )}
              </li>
              <li className="relative">
                <button
                  onClick={() => scrollToSection("courses")}
                  className={`text-base md:text-md xl:text-lg font-medium transition duration-300 ${
                    isActive("/courses")
                      ? "text-primary"
                      : "text-gray-700 hover:text-blue-800"
                  }`}
                >
                  Our Courses
                </button>
                {isActive("/about") && (
                  <div className="absolute -bottom-7 left-1/2 transform -translate-x-1/2">
                    <ActiveNavbar className="w-auto h-auto" />
                  </div>
                )}
              </li>
              <li className="relative">
                <button
                  onClick={() => scrollToSection("about")}
                  className={`text-base md:text-md xl:text-lg font-medium transition duration-300 ${
                    isActive("/about")
                      ? "text-primary"
                      : "text-gray-700 hover:text-blue-800"
                  }`}
                >
                  About Us
                </button>
                {isActive("/about") && (
                  <div className="absolute -bottom-7 left-1/2 transform -translate-x-1/2">
                    <ActiveNavbar className="w-auto h-auto" />
                  </div>
                )}
              </li>
              <li className="relative">
                <button
                  onClick={() => scrollToSection("contact-section")}
                  className={`text-base md:text-md xl:text-lg font-medium transition duration-300 ${
                    isActive("/contact")
                      ? "text-primary"
                      : "text-gray-700 hover:text-blue-800"
                  }`}
                >
                  Contact
                </button>
                {isActive("/contact") && (
                  <div className="absolute -bottom-7 left-1/2 transform -translate-x-1/2">
                    <ActiveNavbar className="w-auto h-auto" />
                  </div>
                )}
              </li>
            </ul>
          </div>

          {/* Auth Buttons */}
          <div className="hidden sl:flex items-center space-x-2 md:space-x-3 lg:space-x-4">
            {loading ? (
              <Loader />
            ) : (
              <>
                {isValid ? (
                  <div className="flex items-center space-x-3">
                    <div className="hidden md:flex items-center">
                      <div className="bg-blue-100 text-primary rounded-full w-8 h-8 flex items-center justify-center mr-2">
                        {user?.name?.charAt(0) || "U"}
                      </div>
                      <span className="text-gray-700 font-medium">
                        {user?.name || "User"}
                      </span>
                    </div>
                    <Link to="/dashboard/home">
                      <button className="px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 text-sm md:text-base text-white bg-primary rounded-md hover:bg-primary/90 transition duration-300">
                        Go To Dashboard
                      </button>
                    </Link>
                  </div>
                ) : (
                  <>
                    <Link to="/login">
                      <button className="px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 text-sm md:text-base text-white bg-primary rounded-md hover:bg-primary/90 transition duration-300">
                        Login
                      </button>
                    </Link>
                    <Link to="/register">
                      <button className="px-6 sm:px-8 md:px-10 py-1.5 sm:py-2 text-sm md:text-base bg-white border border-primary text-primary rounded-md hover:bg-gray-50 transition duration-300">
                        Create account
                      </button>
                    </Link>
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="sl:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-800 hover:bg-gray-100 focus:outline-none"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {/* Icon when menu is closed */}
              {!isMenuOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                /* Icon when menu is open */
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state */}
      {isMenuOpen && (
        <div className="sl:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white shadow-lg">
            {isValid && (
              <div className="flex items-center px-3 py-2 mb-2 border-b border-gray-100">
                <div className="bg-blue-100 text-primary rounded-full w-8 h-8 flex items-center justify-center mr-2">
                  {user?.name?.charAt(0) || "U"}
                </div>
                <span className="text-gray-700 font-medium">
                  {user?.name || "User"}
                </span>
              </div>
            )}
            <button
              onClick={() => scrollToSection("top")}
              className={`block w-full text-left px-3 py-2 rounded-md text-sm xs:text-base font-medium ${
                isActive("/")
                  ? "text-primary bg-gray-50"
                  : "text-gray-700 hover:text-blue-800 hover:bg-gray-50"
              }`}
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection("about")}
              className={`block w-full text-left px-3 py-2 rounded-md text-sm xs:text-base font-medium ${
                isActive("/about")
                  ? "text-primary bg-gray-50"
                  : "text-gray-700 hover:text-blue-800 hover:bg-gray-50"
              }`}
            >
              About Us
            </button>
            <button
              onClick={() => scrollToSection("contact-section")}
              className={`block w-full text-left px-3 py-2 rounded-md text-sm xs:text-base font-medium ${
                isActive("/contact")
                  ? "text-primary bg-gray-50"
                  : "text-gray-700 hover:text-blue-800 hover:bg-gray-50"
              }`}
            >
              Contact
            </button>
            <div className="flex flex-col space-y-2 mt-4 px-3 py-2">
              {isValid ? (
                <Link
                  to="/dashboard/home"
                  className="w-full text-center px-4 py-2 text-sm xs:text-base text-white bg-primary font-medium rounded-md hover:bg-primary/90 transition duration-300"
                >
                  Go To Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="w-full text-center px-4 py-2 text-sm xs:text-base text-white bg-primary font-medium rounded-md hover:bg-primary/90 transition duration-300"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="w-full text-center px-4 py-2 text-sm xs:text-base bg-white border border-primary text-primary font-medium rounded-md hover:bg-gray-50 transition duration-300"
                  >
                    Create account
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
